/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CreateGrnApi } from "../../../../Api/Operations/PurchaseOrders";
import { getPopulateUsers } from "../../../../Redux/actions/accountsactions/getContractComments";
import { useSession } from "../../../../context/SessionContext";
import ActionButton from "../../../Common/ActionButton";

export default function GenerateGrnModal({ isOpen, onClose, poData, onSuccess }) {
  const dispatch = useDispatch();
  const populateUsers = useSelector((s) => s.getAllPopulateUsers?.data || []);
  // Still needed below to offer the current user as an approver option — only the
  // project source changed.
  const { session } = useSession();
  const dropdownRef = useRef(null);
  // This modal always opens against one specific PO — use its own project, not
  // whatever's active in the top nav. Old: const projectId = session?.ProjectId;
  const projectId = poData?.ProjectId;
  const toInputDateTime = (iso) => {
    if (!iso) {
      return "";
    }
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${day}T${hh}:${mm}`;
  };
  const [lines, setLines] = useState([]);
  const [remarksMap, setRemarksMap] = useState({});
  const [quantityMap, setQuantityMap] = useState({});
  // const [qualityMap, setQualityMap] = useState({});
  const [receivedDate, setReceivedDate] = useState(() => toInputDateTime(new Date().toISOString()));
  const [narration, setNarration] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showApproverDropdown, setShowApproverDropdown] = useState(false);
  const [approverUserIds, setApproverUserIds] = useState([]);
  const [approverSearchTerm, setApproverSearchTerm] = useState("");
  const filteredUsers = [
    ...populateUsers,
    // {
    //   Id: session?.LoginId,
    //   FullName: session?.UserName,
    //   Email: session?.Email,
    // },
  ]?.filter(
    (user) =>
      user?.FullName?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
      user?.Email?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
      user?.Id?.toLowerCase().includes(approverSearchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (!poData) {
      return;
    }
    const initialLines = poData.Lines || [];
    setLines(initialLines);
    dispatch(getPopulateUsers(projectId));

    const initQty = {};
    const initQuality = {};
    const initRemarks = {};
    initialLines.forEach((l) => {
      const id = l.Id || l.poLineId || "";
      initQty[id] = l.RemainingQuantity || l.quantity || 0;
      initQuality[id] = "Good";
      initRemarks[id] = "";
    });
    setQuantityMap(initQty);
    // setQualityMap(initQuality);
    setRemarksMap((p) => ({ ...initRemarks, ...p }));
    setReceivedDate(toInputDateTime(poData?.ReceivedDate || new Date().toISOString()));
  }, [poData, dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowApproverDropdown(false);
      }
    }
    if (showApproverDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showApproverDropdown]);

  if (!isOpen) {
    return null;
  }

  const handleRemarkChange = (lineId, value) => {
    setRemarksMap((p) => ({ ...p, [lineId]: value }));
  };

  const handleQuantityChange = (lineId, value) => {
    setQuantityMap((p) => ({
      ...p,
      [lineId]: value === "" ? "" : Number(value),
    }));
  };

  // const handleQualityChange = (lineId, value) => {
  //   setQualityMap((p) => ({ ...p, [lineId]: value }));
  // };
  function removeApprover(userId) {
    setApproverUserIds((prev) => prev.filter((id) => id !== userId));
  }
  function toggleApprover(userId) {
    setApproverUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  const validate = () => {
    if (!narration || narration.trim() === "") {
      toast.info("Narration is required");
      return false;
    }
    if (!receivedDate) {
      toast.info("Received date is required");
      return false;
    }
    // if (approverUserIds.length === 0) {
    //   toast.info("Please select at least one approver");
    //   {return;}
    // }
    for (const l of lines) {
      const id = l.Id || l.poLineId;
      const qtyReceived = Number(quantityMap[id] ?? 0);
      const maxQty = Number(l.RemainingQuantity ?? l.quantity ?? 0);
      const remark = remarksMap[id] || "";

      if (qtyReceived > maxQty) {
        toast.info("Quantity received cannot exceed received ordered quantity");
        return false;
      }
      if (qtyReceived > 0 && (!remark || remark.trim() === "")) {
        toast.info("Remarks required for each line item");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        purchaseOrderId: poData.Id || poData.PurchaseOrderId,
        grnDate: new Date(receivedDate).toISOString(),
        narration,
        lines: (lines || []).map((l) => {
          const id = l.Id || l.poLineId;
          return {
            poLineId: id,
            quantityReceived: Number(quantityMap[id] ?? 0),
            remarks: remarksMap[id] || "",
          };
        }),
        approvers: approverUserIds.map((approverUserId) => ({
          approverUserId,
        })),
      };

      const res = await CreateGrnApi(payload);
      const json = res?.data || res;
      if (json?.IsSuccess) {
        toast.success(json.Message || "GRN created");
        onSuccess?.();
      } else {
        toast.error(json?.Message || "Failed to create GRN");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating GRN");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modalscreen" style={{ display: "block", height: "100vh", overflow: "auto" }}>
      <div className="modal-content-screen1" style={{ overflow: "visible" }}>
        <div className="row sticky-topNew">
          <div className="col-12 page_heading">
            <h4 className="text-center mb-0">Generate GRN</h4>
            <span className="close pr-2" onClick={onClose}>
              &times;
            </span>
          </div>
        </div>

        <div className="pl-3 pr-3">
          <div className="table-responsive_modal mt-2">
            <table className="table table-borderless m-0">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Description</th>
                  <th>Qty Remaining</th>
                  <th>Qty Received</th>
                  {/* <th>Quality</th> */}
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(lines || []).map((l, i) => {
                  const id = l.Id || l.poLineId || i;
                  return (
                    <tr key={id}>
                      <td className="table_data">{i + 1}</td>
                      <td className="table_data">{l.ItemDescription}</td>
                      <td className="table_data">{l.RemainingQuantity}</td>
                      <td className="table_data">
                        <input
                          className="input_styling"
                          type="number"
                          min={0}
                          max={l.RemainingQuantity}
                          value={quantityMap[id] ?? ""}
                          onChange={(e) => handleQuantityChange(id, e.target.value)}
                        />
                        <span className="estaric">*</span>
                      </td>
                      {/* <td className="table_data">
                        <select
                          className="input_styling"
                          value={qualityMap[id] || "Good"}
                          onChange={(e) =>
                            handleQualityChange(id, e.target.value)
                          }
                        >
                          <option value="Good">Good</option>
                          <option value="Damaged">Damaged</option>
                          <option value="Other">Other</option>
                        </select>
                      </td> */}
                      <td className="table_data d-flex">
                        <textarea
                          className="input_address"
                          rows={2}
                          value={remarksMap[id] || ""}
                          onChange={(e) => handleRemarkChange(id, e.target.value)}
                        />
                        <span className="estaric">*</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="row">
            <div className="col-md-2 mt-md-4 text-left p-0">
              <label className="input_label m-0">Narration:</label>
            </div>
            <div className="col-md-3 mt-md-4 p-0 d-flex">
              <textarea
                className="input_address"
                rows={3}
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
              />
              <span className="estaric">*</span>
            </div>
            <div className="col-md-2 mt-md-4 text-left p-0"></div>

            <div className="col-md-2 mt-md-4 text-left p-0">
              <label className="input_label m-0">Approver Users:</label>
            </div>
            <div
              className="col-md-3 mt-md-4 p-0"
              style={{ position: "relative" }}
              data-approver-section
              ref={dropdownRef}
            >
              <div className="d-flex">
                {/* Selected Approvers Display */}
                <div
                  className="input_styling ml-md-3"
                  style={{
                    // width: "100%",
                    minHeight: "82px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    alignItems: "start",
                    cursor: "pointer",
                    padding: "8px",
                    boxSizing: "border-box",
                    overflow: "auto",
                    minWidth: 0,
                    maxHeight: "120px",
                  }}
                  onClick={() => setShowApproverDropdown((prev) => !prev)}
                >
                  {approverUserIds.length === 0 ? (
                    <span style={{ color: "#6c757d", fontSize: "14px" }}>Select approvers...</span>
                  ) : (
                    approverUserIds.map((userId) => {
                      const user = [
                        ...populateUsers,
                        {
                          Id: session?.LoginId,
                          FullName: session?.UserName,
                          Email: session?.Email,
                        },
                      ]?.find((u) => u?.Id === userId);
                      return (
                        <span
                          key={userId}
                          style={{
                            backgroundColor: "#e9ecef",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flex: "0 1 auto",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "inline-block",
                              maxWidth: "120px",
                            }}
                          >
                            {user ? user.FullName : userId}
                          </span>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              removeApprover(userId);
                            }}
                            style={{
                              cursor: "pointer",
                              color: "#dc3545",
                              fontWeight: "bold",
                            }}
                          >
                            ×
                          </span>
                        </span>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Searchable Dropdown */}
              {showApproverDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    background: "#fff",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    maxHeight: "220px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={approverSearchTerm}
                      onChange={(e) => setApproverSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div>
                    {filteredUsers?.map((user) => (
                      <div
                        key={user.Id}
                        onClick={() => toggleApprover(user.Id)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          backgroundColor: approverUserIds.includes(user.Id)
                            ? "#e9ecef"
                            : "transparent",
                          borderBottom: "1px solid #f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={approverUserIds.includes(user.Id)}
                          readOnly
                          style={{ margin: 0 }}
                        />
                        <div>
                          <div style={{ fontWeight: "500", fontSize: "13px" }}>{user.FullName}</div>
                          <div style={{ fontSize: "12px", color: "#6c757d" }}>{user.Email}</div>
                        </div>
                      </div>
                    ))}
                    {filteredUsers.length === 0 && (
                      <div
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "#6c757d",
                        }}
                      >
                        No users found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-2 mt-md-4 text-left p-0">
              <label className="input_label m-0">Received Date</label>
            </div>
            <div className="col-md-3 mt-md-4 p-0">
              <input
                type="datetime-local"
                className="input_styling"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
              <span className="estaric">*</span>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-12 d-flex justify-content-end" style={{ gap: 8 }}>
              <ActionButton
                text="Cancel"
                variant="cancel"
                onClick={onClose}
                disabled={submitting}
              />
              <ActionButton
                text={submitting ? "Please wait..." : "Create GRN"}
                variant="save"
                onClick={handleSubmit}
                disabled={submitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
